import type { DefinitionResponse, PSetResponse } from '../generated';
import type { TcPsetApiService } from '../tc-pset-api-service';
import {
  getDefinitionName,
  makeDefinitionKey,
  toDisplayProperties,
} from './custom-pset-mapper';
import type {
  CustomPsetInstance,
  CustomPsetReadActions,
  ListCustomPsetInstancesParams,
} from './custom-pset.types';

// Matches the pset service's max page size; documented alongside the API contract.
const PAGE_SIZE = 500;

/**
 * @intent Query-key builder for `useQuery` consumers loading custom pset
 * instances. Sorting the links makes the key insensitive to selection order,
 * so re-selecting the same entities in a different order hits the cache.
 */
export function customPsetInstancesQueryKey(
  regionKey: string,
  links: readonly string[]
): readonly [namespace: string, regionKey: string, sortedLinks: string[]] {
  return [
    'custom-pset-instances',
    regionKey,
    [...links].sort((a, b) => a.localeCompare(b)),
  ];
}

/**
 * Opaque cache handle passed to {@link createCustomPsetReadActions} to
 * preserve definition lookups across factory rebuilds. Callers should treat
 * the contents as internal.
 */
export type CustomPsetDefinitionCache = Map<string, DefinitionResponse>;

/** Creates a fresh, empty {@link CustomPsetDefinitionCache}. */
export function createCustomPsetDefinitionCache(): CustomPsetDefinitionCache {
  return new Map();
}

export interface CustomPsetReadActionsLogger {
  warn(message: string, context?: Record<string, unknown>): void;
}

export interface CreateCustomPsetReadActionsOptions {
  getPsetApi(): Promise<TcPsetApiService | undefined>;
  /**
   * Optional externally-owned definition cache. When provided, definitions
   * survive across factory instances that share the same Map — enabling
   * callers to rebuild the factory (e.g. to bind a fresh `getPsetApi`) without
   * losing cached schema/i18n data.
   *
   * The caller owns Map identity and MUST use a Map that is scoped correctly
   * to the region — sharing one Map across regions would cross-contaminate
   * definitions with the same `libId::defId`.
   *
   * If omitted, a fresh internal Map is used and the cache lives only for
   * this factory instance.
   */
  definitionCache?: CustomPsetDefinitionCache;
  /**
   * Optional logger. Partial-failure warnings during pagination are emitted here.
   * Defaults to no-op so this module stays framework-agnostic.
   */
  logger?: CustomPsetReadActionsLogger;
}

/**
 * @intent Fetches custom pset instances for a set of link FRNs and joins each
 * instance with its definition (schema + i18n) so the UI can render typed rows.
 *
 * Cache lifetime:
 *   - When {@link CreateCustomPsetReadActionsOptions.definitionCache} is
 *     provided, the caller owns the cache and can share it across rebuilds of
 *     this factory (e.g. across consumer remounts).
 *   - Otherwise the internal cache lives for this factory instance only.
 *   - Definitions edited elsewhere (rename, add prop) will show stale labels
 *     until the owning cache is cleared or replaced. This module does NOT
 *     invalidate on API-instance changes — that concern belongs to the caller
 *     that owns the cache lifecycle.
 */
export function createCustomPsetReadActions(
  options: CreateCustomPsetReadActionsOptions
): CustomPsetReadActions {
  const definitionCache =
    options.definitionCache ?? createCustomPsetDefinitionCache();

  const listInstances = async (
    params: ListCustomPsetInstancesParams
  ): Promise<CustomPsetInstance[]> => {
    const psetApi = await options.getPsetApi();
    if (psetApi === undefined || params.links.length === 0) return [];

    const instances = await listPsetResponses(psetApi, params, options.logger);
    await loadDefinitionsForInstances(psetApi, instances, definitionCache);

    return instances.map((pset) => {
      const definition = definitionCache.get(
        makeDefinitionKey(pset.libId, pset.defId)
      );
      return {
        link: pset.link,
        libId: pset.libId,
        defId: pset.defId,
        psetName: getDefinitionName(definition) ?? pset.defId,
        version: pset.v,
        properties: toDisplayProperties(definition, pset.props),
      };
    });
  };

  return { listInstances };
}

async function listPsetResponses(
  psetApi: TcPsetApiService,
  params: ListCustomPsetInstancesParams,
  logger?: CustomPsetReadActionsLogger
): Promise<PSetResponse[]> {
  if (params.links.length === 0) return [];

  // Partial-failure policy: if some links fail we still surface the fulfilled
  // pages. Only when every link fails do we re-throw so the outer query flips
  // into `isError` and the caller can toast/log at the boundary.
  const pagesByLink = await Promise.allSettled(
    params.links.map((link) => listInstancesForLink(psetApi, link))
  );

  const rejected = pagesByLink.filter(
    (result): result is PromiseRejectedResult => result.status === 'rejected'
  );
  if (rejected.length === pagesByLink.length && rejected.length > 0) {
    throw new AggregateError(
      rejected.map((result) => result.reason),
      'All custom pset link fetches failed'
    );
  }
  if (rejected.length > 0) {
    logger?.warn('Some custom pset link fetches failed', {
      failedCount: rejected.length,
      totalCount: pagesByLink.length,
    });
  }

  return pagesByLink
    .filter(
      (result): result is PromiseFulfilledResult<PSetResponse[]> =>
        result.status === 'fulfilled'
    )
    .flatMap((result) => result.value)
    .filter((pset) => pset.deleted !== true);
}

async function listInstancesForLink(
  psetApi: TcPsetApiService,
  link: string
): Promise<PSetResponse[]> {
  const instances: PSetResponse[] = [];
  let skiptoken: string | undefined;

  do {
    const page = await psetApi.listPsetsByLink({
      path: { link },
      query: {
        top: PAGE_SIZE,
        ...(skiptoken ? { skiptoken } : {}),
      },
    });
    instances.push(...page.items);
    skiptoken = getSkipToken(page.next);
  } while (skiptoken !== undefined);

  return instances;
}

async function loadDefinitionsForInstances(
  psetApi: TcPsetApiService,
  instances: readonly PSetResponse[],
  cache: CustomPsetDefinitionCache
): Promise<void> {
  const missing = new Map<string, PSetResponse>(
    instances
      .map((instance): [string, PSetResponse] => [
        makeDefinitionKey(instance.libId, instance.defId),
        instance,
      ])
      .filter(([key]) => !cache.has(key))
  );
  if (missing.size === 0) return;

  const response = await psetApi.batchGet({
    body: {
      defs: [...missing.values()].map((instance) => ({
        libId: instance.libId,
        defId: instance.defId,
      })),
    },
  });

  for (const definition of response.responses.defs ?? []) {
    cache.set(makeDefinitionKey(definition.libId, definition.id), definition);
  }
}

function getSkipToken(next: string | undefined): string | undefined {
  if (next === undefined) return undefined;
  try {
    const url = new URL(next);
    return url.searchParams.get('skiptoken') ?? undefined;
  } catch {
    return getQueryParam(next, 'skiptoken');
  }
}

function getQueryParam(value: string, key: string): string | undefined {
  const query = value.split('?')[1];
  if (!query) return undefined;
  return new URLSearchParams(query).get(key) ?? undefined;
}
